import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PenLine, X } from "lucide-react";
import RotatingPhoto from "@/components/RotatingPhoto";
import { MaskedLine, Reveal, SectionTag } from "@/components/Reveal";
import { JOURNAL } from "@/data";

const Journal = () => {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  const entry = active !== null ? JOURNAL[active] : null;

  return (
    <motion.main
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="pt-28"
      data-testid="journal-page"
    >
      <header className="px-6 md:px-12">
        <SectionTag no="07" label="JOURNAL / STORIES" />
        <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl md:text-8xl" data-testid="journal-title">
          <MaskedLine delay={0.1}><span>NOTES FROM</span></MaskedLine>
          <MaskedLine delay={0.25}><span className="text-burgundy">THE JOURNEY</span></MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-xl font-serif text-xl italic leading-relaxed text-ash md:text-2xl">
            Lessons from organizations, behind-the-scenes, reflections, dan cerita-cerita kecil di sepanjang jalan.
          </p>
        </Reveal>
      </header>

      <section className="px-6 py-20 md:px-12" data-testid="journal-list">
        <div className="grid gap-10 md:grid-cols-2">
          {JOURNAL.map((j, i) => (
            <Reveal key={j.slug} delay={i * 0.07}>
              <article
                data-testid={`journal-entry-${j.slug}`}
                data-cursor="READ"
                onClick={() => setActive(i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setActive(i)}
                className="group cursor-pointer border border-ink/15 transition-all duration-300 hover:-translate-y-1 hover:shadow-[10px_10px_0_0_#0F1115]"
              >
                <RotatingPhoto images={j.coverPool} ratio="aspect-[16/10]" showDots={false} label={j.category} />
                <div className="p-7">
                  <div className="flex items-center justify-between font-display text-[9px] font-bold uppercase tracking-[0.25em] text-burgundy">
                    <span>{String(i + 1).padStart(2, "0")}</span>
                    {j.date && <span className="text-ink/40">{j.date}</span>}
                  </div>
                  <h2 className="mt-3 font-display text-2xl font-black uppercase tracking-tight md:text-3xl">{j.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-ash">{j.excerpt}</p>
                  <span className="mt-5 flex items-center gap-2 font-display text-[10px] font-bold uppercase tracking-[0.25em] text-ink/50 transition-colors group-hover:text-burgundy">
                    <PenLine className="h-3.5 w-3.5" /> Baca ceritanya →
                  </span>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {entry && (
          <motion.div
            data-testid="journal-reader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] overflow-y-auto bg-paper"
          >
            <button
              data-testid="journal-close"
              onClick={() => setActive(null)}
              aria-label="Tutup"
              className="fixed right-6 top-6 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-ink text-paper transition-colors hover:bg-burgundy"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.article
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto max-w-3xl px-6 py-24"
            >
              <div className="flex items-center gap-4 font-display text-[10px] font-bold uppercase tracking-[0.3em] text-burgundy">
                <span>{entry.category}</span>
                {entry.date && <><span className="text-ink/30">/</span><span className="text-ink/50">{entry.date}</span></>}
              </div>
              <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter md:text-6xl" data-testid="journal-reader-title">
                {entry.title}
              </h1>
              <div className="mt-10">
                <RotatingPhoto images={entry.coverPool} ratio="aspect-[16/9]" className="border border-ink/15" />
              </div>
              <div className="mt-12 space-y-6">
                {entry.body.map((p, i) => (
                  <p key={i} className={`leading-relaxed ${i === 0 ? "font-serif text-2xl italic text-burgundy md:text-3xl" : "text-ink/80 md:text-lg"}`}>
                    {p}
                  </p>
                ))}
              </div>
              <p className="mt-14 border-t border-ink/10 pt-6 font-serif text-lg italic text-ash">
                — Kin Hakan
              </p>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.main>
  );
};

export default Journal;
